import { Injectable } from '@angular/core';
import * as THREE from '@phdsoft/three';
import { Asset } from 'src/app/platform/busines-data/Framework/Asset';
import { AssetState } from 'src/app/platform/busines-data/Framework/asset-state';
import { AssetManager } from 'src/app/asset-manager/asset-manager';

/**
 * AssetStateCapture is responsible for creating immutable snapshots of the
 * current 3D scene state (camera, region/section visibility, mode, etc.)
 * and for detecting whether a new snapshot is a duplicate of the current one.
 *
 * Extracted from DTSelectionService (Phase 2 -- CAPT) to decouple state
 * snapshot creation from the 2700-line god class.
 */
@Injectable({ providedIn: 'root' })
export class AssetStateCapture {

    private camera: THREE.PerspectiveCamera;
    private controls: any; // OrbitControls -- typed as any to match DTSelectionService pattern
    private assetManager: AssetManager;

    // -------------------------------------------------------------------
    // Initialization
    // -------------------------------------------------------------------

    /**
     * Stores references to the scene camera and orbit controls.
     * Called once when the 3D view initializes.
     * @param camera The scene camera (always PerspectiveCamera in this codebase).
     * @param controls The orbit controls instance (provides `.target` vector).
     */
    public setCameras(camera: THREE.Camera, controls: any): void {
        this.camera = camera as THREE.PerspectiveCamera;
        this.controls = controls;
    }

    /**
     * Stores a reference to the AssetManager for reading current region/section context.
     * Called once during initialization.
     * @param assetManager The application's AssetManager instance.
     */
    public setAssetManager(assetManager: AssetManager): void {
        this.assetManager = assetManager;
    }

    // -------------------------------------------------------------------
    // Public capture methods
    // -------------------------------------------------------------------

    /**
     * Creates a full snapshot of the current 3D scene state.
     * All vectors are cloned so that subsequent camera movement does not
     * retroactively modify the snapshot.
     *
     * @param asset The active Asset whose regions/mode are being captured.
     * @returns A new AssetState with action='Select', fully populated.
     */
    public captureCurrentState(asset: Asset): AssetState {
        const state = new AssetState();

        // Clone camera vectors -- mutations to the live camera must not affect the snapshot
        state.position = this.camera.position.clone();
        state.target = this.controls.target.clone();
        state.cameraUp = this.camera.up.clone();

        // Copy scalar camera fields
        state.cameraFov = this.camera.fov;
        state.cameraZoom = this.camera.zoom;
        state.cameraNear = this.camera.near;
        state.cameraFar = this.camera.far;
        state.cameraAspect = this.camera.aspect;

        // Region/section visibility map
        state.regions = this.captureRegionVisibility(asset);

        // Mode and sub-modes
        state.mode = asset.mode;
        state.visualizationMode = asset.visualizationMode;
        state.viewMode = asset.viewMode as any;

        // Current context from AssetManager
        state.regionId = this.assetManager?.currentRegion?.Guid;
        state.sectionId = this.assetManager?.currentSection?.Guid;

        // Asset bounding box
        state.box3 = asset.box3;

        // Default action for regular captures
        state.action = 'Select';
        state.object = null;
        state.objectVisibility = undefined;

        return state;
    }

    /**
     * Creates a snapshot specifically for element-hide operations.
     * Delegates to captureCurrentState(), then overrides hide-specific fields.
     *
     * @param asset The active Asset.
     * @param object The THREE.Object3D being hidden.
     * @param elementId The Guid of the element being hidden.
     * @param regionId The Guid of the region containing the element.
     * @param sectionId The Guid of the section containing the element.
     * @returns A new AssetState with action='Hide', preserving the current asset mode.
     */
    public captureHideState(
        asset: Asset,
        object: THREE.Object3D,
        elementId: string,
        regionId: string,
        sectionId: string,
    ): AssetState {
        const state = this.captureCurrentState(asset);

        // Override for hide action
        state.action = 'Hide';
        state.object = object;
        state.objectVisibility = object?.visible;
        state.elementId = elementId;
        state.regionId = regionId;
        state.sectionId = sectionId;

        return state;
    }

    /**
     * Determines whether `newState` is a duplicate of `currentState`.
     *
     * Duplicate semantics:
     * - Camera-only changes are duplicates (camera is captured but does NOT trigger new undo points).
     * - Different action types are NEVER duplicates (Select vs Hide).
     * - For Hide actions: duplicates require same object reference AND same objectVisibility.
     * - For Select actions: duplicates require same mode, visualizationMode, AND region/section visibility.
     *
     * Element-level duplicate detection is inherently handled by action-type separation:
     * every element hide goes through captureHideState() (action='Hide'), so it is never
     * a duplicate of a Select state. Two Hide states are only duplicates if they target the
     * same object with the same visibility.
     *
     * @param newState The newly captured state.
     * @param currentState The state currently at the cursor position.
     * @returns true if the new state should be skipped (duplicate).
     */
    public isDuplicate(newState: AssetState, currentState: AssetState): boolean {
        // Null safety
        if (!newState || !currentState) {
            return false;
        }

        // Never duplicate against the root state
        if (currentState.isRoot) {
            return false;
        }

        // Different action types are never duplicates
        if (newState.action !== currentState.action) {
            return false;
        }

        // Hide action: compare object identity and visibility
        if (newState.action === 'Hide') {
            return newState.object === currentState.object
                && newState.objectVisibility === currentState.objectVisibility;
        }

        // Select action: compare mode
        if (newState.mode !== currentState.mode) {
            return false;
        }

        // Compare visualizationMode
        if (newState.visualizationMode !== currentState.visualizationMode) {
            return false;
        }

        // Compare region/section visibility (deep comparison)
        if (!AssetState.compareRegions(newState.regions, currentState.regions)) {
            return false;
        }

        // All checks passed -- this is a duplicate
        return true;
    }

    // -------------------------------------------------------------------
    // Private helpers
    // -------------------------------------------------------------------

    /**
     * Builds a visibility map of all regions and their sections.
     * Always captures structure regions. Light-model regions are captured ONLY
     * in MODEL mode: they share Guids with the structure regions (see
     * AssetLoader.setSectionGroups(lightCampaign, region.id)), so outside MODEL
     * they would overwrite the structure entries with the "all light regions
     * hidden" state that selectRegion() leaves behind, and restoring that
     * snapshot would hide every structure region. Matches prod's
     * computeAssetState behaviour.
     *
     * @param asset The active Asset.
     * @returns A map of region Guid -> { visible, sections: { sectionGuid -> visible } }
     */
    private captureRegionVisibility(
        asset: Asset,
    ): { [id: string]: { visible?: boolean; sections?: { [id: string]: boolean } } } {
        const regs: { [id: string]: { visible?: boolean; sections?: { [id: string]: boolean } } } = {};

        // Always capture structure regions
        const structureRegions = asset.GetAllStructureRegions();
        if (structureRegions) {
            for (const region of structureRegions) {
                const secs: { [id: string]: boolean } = {};
                if (region.sections) {
                    for (const section of region.sections) {
                        secs[section.Guid] = section.visible;
                    }
                }
                regs[region.Guid] = {
                    visible: region.visible,
                    sections: secs,
                };
            }
        }

        // Light-model regions: MODEL mode only (see JSDoc above).
        if (asset.isLightModel && asset.mode === 'MODEL') {
            const lightRegions = asset.GetLightModelRegions();
            if (lightRegions) {
                for (const region of lightRegions) {
                    const secs: { [id: string]: boolean } = {};
                    if (region.sections) {
                        for (const section of region.sections) {
                            secs[section.Guid] = section.visible;
                        }
                    }
                    regs[region.Guid] = {
                        visible: region.visible,
                        sections: secs,
                    };
                }
            }
        }

        return regs;
    }
}
